import { useState } from 'react';
import type { HedgeAction, ScenarioAnalysis } from '@prm/shared';
import { pct, usd } from '../format';
import { PanelHead } from './Panel';

const KIND_LABEL: Record<HedgeAction['kind'], string> = {
  trim: 'TRIM',
  'cluster-degross': 'DE-GROSS',
  'index-put': 'PUT',
  'index-put-spread': 'SPREAD',
  collar: 'COLLAR',
};

const KIND_TONE: Record<HedgeAction['kind'], 'free' | 'cost'> = {
  trim: 'free',
  'cluster-degross': 'free',
  'index-put': 'cost',
  'index-put-spread': 'cost',
  collar: 'cost',
};

/**
 * Best mitigations, cheapest risk reduction first.
 *
 * The trade-off line is the part a ranked list usually omits. Every action here
 * has a downside — a trim gives up upside, a put decays, a collar caps the rally
 * — and an action list that only shows benefits is a sales sheet.
 */
export function MitigationPanel({ scenario }: { scenario: ScenarioAnalysis }) {
  const [open, setOpen] = useState<string | null>(null);
  const { hedge } = scenario;

  return (
    <section className="panel">
      <PanelHead
        title="Best mitigations"
        description="Sorted by risk reduction per unit of cost."
        sub={
          <span className="panel__sub">
            {hedge.requiredReduction > 0
              ? `${hedge.currentBetaAdjNet.toFixed(2)}× → ${hedge.targetBetaAdjNet.toFixed(2)}×`
              : 'no de-risking required'}
          </span>
        }
      />

      {hedge.actions.length === 0 ? (
        <div className="placeholder-note">
          Nothing to mitigate — the book is inside its band and no position breaches a single-name limit.
        </div>
      ) : (
        <div className="action-list">
          {hedge.actions.map((a) => {
            const expanded = open === a.id;
            const donation = a.flags.some((f) => f.id.startsWith('long-vol-donation'));

            return (
              <article className="action-row" key={a.id}>
                <span className={`action-kind is-${KIND_TONE[a.kind]}`}>{KIND_LABEL[a.kind]}</span>

                <div style={{ minWidth: 0 }}>
                  <div className="action-title">
                    <span>{a.label}</span>
                    {a.alternativeTo?.length ? <span className="minichip">alt</span> : null}
                    {donation ? <span className="minichip minichip--warn">long vol</span> : null}
                  </div>
                  <p className="action-detail">{a.instruction}</p>
                  <p className="action-why">
                    <span className="action-label">Why</span>
                    {a.rationale}
                  </p>
                  <p className="action-tradeoff">
                    <span className="action-label">Trade-off</span>
                    {a.tradeoff}
                  </p>

                  <button
                    className="button button--sm"
                    style={{ marginTop: 9 }}
                    onClick={() => setOpen(expanded ? null : a.id)}
                    aria-expanded={expanded}
                  >
                    {expanded ? 'Hide costing' : 'How this is costed'}
                  </button>

                  {expanded ? (
                    <div className="action-tradeoff" style={{ borderLeftColor: 'var(--accent)' }}>
                      <div className="detail__label">Cost basis</div>
                      <p className="detail__text">{a.cost.note}</p>
                      {a.legs?.length ? (
                        <>
                          <div className="detail__label">Legs</div>
                          {a.legs.map((leg, i) => (
                            <p className="detail__text" key={i}>
                              {leg.side} {leg.contracts} × {leg.underlying} {usd(leg.strike, 0)} {leg.type},{' '}
                              {leg.expiryDays}d, est. {usd(leg.premium, 2)}/share, delta {leg.delta.toFixed(3)}
                            </p>
                          ))}
                        </>
                      ) : null}
                      {a.flags.map((f) => (
                        <p className="detail__text" key={f.id}>
                          <strong>{f.title}.</strong> {f.detail}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="impact">
                  <strong className={a.resulting.insideBand ? 'text-good' : 'text-bad'}>
                    {a.resulting.betaAdjNet.toFixed(2)}×
                  </strong>
                  <span>beta-adj net</span>
                  <strong>{pct(a.resulting.worstStressPct)}</strong>
                  <span>worst case</span>
                  <span className={donation ? 'text-warn' : ''}>
                    {a.cost.premium === 0 ? 'no premium' : usd(a.cost.premium)}
                    {a.cost.annualizedPctOfBook > 0 ? ` · ${pct(a.cost.annualizedPctOfBook, 2)}/yr` : ''}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {hedge.protectedPositions.length > 0 ? (
        <>
          <PanelHead title="Left alone" description="Positions the ranking will not touch, and why." />
          <div className="trigger-list">
            {hedge.protectedPositions.map((p) => (
              <div className="trigger-row" key={p.ticker}>
                <span className={`minichip ${p.isDiversifier ? 'minichip--ok' : 'minichip--warn'}`}>
                  {p.isDiversifier ? 'DIV' : 'HELD'}
                </span>
                <div className="row-detail" style={{ marginTop: 0 }}>
                  <span className="tkr">{p.ticker}</span> {p.reason}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <div className="footnote">
        Ordered cheapest risk reduction first: trim, de-gross, index options, collars. Option premiums are modelled
        estimates rather than quotes, and trim sizes are ranges because beta is estimated.
      </div>
    </section>
  );
}
